/**
 * Boomfection Landing — Pickers Module
 * Color picker, virus picker, random fill display, slot configurator
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

import { PLAYER_COLORS } from '/js/game/GameCore.js';
import { VIRUS_TYPES } from '/js/game/VirusRenderer.js';
import { VIRUS_TYPE_IDS } from '/js/game/shared/VirusTypes.js';
import { DIFFICULTY_LABELS, SLOT_TYPES } from './LandingGameUI.js';
import { t } from '/js/i18n.js';

/**
 * Sets up a color picker in the given container
 * @param {string} containerId - DOM element ID
 * @param {string} initialColor - initially selected color ID
 * @param {string[]} takenColors - list of taken color IDs
 * @param {Function} onSelect - callback(colorId) when color is selected
 * @returns {string} the auto-selected color ID
 */
export function setupColorPicker(containerId, initialColor, takenColors = [], onSelect) {
    const container = document.getElementById(containerId);
    if (!container) return initialColor;
    container.textContent = '';

    let autoSelect = initialColor;
    if (takenColors.includes(initialColor)) {
        const freeColor = PLAYER_COLORS.find((c) => !takenColors.includes(c.id));
        autoSelect = freeColor ? freeColor.id : initialColor;
    }

    PLAYER_COLORS.forEach((c) => {
        const isTaken = takenColors.includes(c.id);
        const opt = document.createElement('div');
        opt.className = `color-option${c.id === autoSelect ? ' selected' : ''}${isTaken ? ' taken' : ''}`;
        opt.style.background = c.hex;
        opt.dataset.colorId = c.id;
        opt.title = isTaken ? `${c.name} ${t('lobby.colorTaken')}` : c.name;

        if (!isTaken) {
            opt.onclick = () => {
                container.querySelectorAll('.color-option').forEach((el) => el.classList.remove('selected'));
                opt.classList.add('selected');
                if (onSelect) onSelect(c.id);
            };
        }
        container.appendChild(opt);
    });

    return autoSelect;
}

/**
 * Gets the currently selected color from a picker container
 * @param {string} containerId
 * @returns {string} color ID
 */
export function getSelectedColor(containerId) {
    const container = document.getElementById(containerId);
    const selected = container?.querySelector('.color-option.selected');
    return selected?.dataset.colorId || 'red';
}

/**
 * Sets up a virus type picker in the given container
 * @param {string} containerId
 * @param {string} initialType
 * @param {Function} onSelect - callback(virusId)
 */
export function setupVirusPicker(containerId, initialType, onSelect) {
    const container = document.getElementById(containerId);
    if (!container) return;
    container.textContent = '';

    // Kanonische Reihenfolge/Menge aus der geteilten Quelle; Render-Props (Icon)
    // aus VirusRenderer, Anzeigename aus i18n.
    VIRUS_TYPE_IDS.forEach((id) => {
        const type = VIRUS_TYPES[id];
        if (!type) return;
        const label = t(`lobby.virusTypes.${id}`) || type.name;
        const opt = document.createElement('div');
        opt.className = `virus-option${type.id === initialType ? ' selected' : ''}`;
        opt.dataset.virusId = type.id;
        opt.title = label;

        const iconSpan = document.createElement('span');
        iconSpan.className = 'virus-icon';
        iconSpan.textContent = type.icon;
        const nameSpan = document.createElement('span');
        nameSpan.className = 'virus-name';
        nameSpan.textContent = label;
        opt.appendChild(iconSpan);
        opt.appendChild(nameSpan);

        opt.onclick = () => {
            container.querySelectorAll('.virus-option').forEach((el) => el.classList.remove('selected'));
            opt.classList.add('selected');
            if (onSelect) onSelect(type.id);
        };
        container.appendChild(opt);
    });
}

/**
 * Updates the random fill range display text
 */
export function updateRandomFillDisplay() {
    const randomFillMin = document.getElementById('random-fill-min');
    const randomFillMax = document.getElementById('random-fill-max');
    const randomFillValue = document.getElementById('random-fill-range-value');
    if (randomFillMin && randomFillMax && randomFillValue) {
        randomFillValue.textContent = `${parseInt(randomFillMin.value)}-${parseInt(randomFillMax.value)}`;
    }
}

/**
 * Sets up the slot configurator for game creation.
 * Returns the slotConfigs array for use by the orchestrator.
 * @param {number} maxPlayers
 * @returns {Object[]} slotConfigs array
 */
export function setupSlotConfigurator(maxPlayers) {
    const container = document.getElementById('slot-configurator');
    container.textContent = '';
    const slotConfigs = [];

    for (let i = 0; i < maxPlayers; i++) {
        const isHost = i === 0;
        slotConfigs.push({ index: i, type: SLOT_TYPES.PLAYER, colorId: null, aiDifficulty: 'normal' });

        const slotEl = document.createElement('div');
        slotEl.className = 'slot-config-item';
        slotEl.dataset.slotIndex = i;

        if (isHost) {
            const label = document.createElement('div');
            label.className = 'slot-config-label';
            const numSpan = document.createElement('span');
            numSpan.className = 'slot-number';
            numSpan.textContent = `Slot ${i + 1}`;
            const badgeSpan = document.createElement('span');
            badgeSpan.className = 'slot-badge host';
            badgeSpan.textContent = t('lobby.youHost');
            label.appendChild(numSpan);
            label.appendChild(badgeSpan);

            const info = document.createElement('div');
            info.className = 'slot-config-info';
            info.textContent = t('lobby.yourSlot');

            slotEl.appendChild(label);
            slotEl.appendChild(info);
        } else {
            const label = document.createElement('div');
            label.className = 'slot-config-label';
            const numSpan = document.createElement('span');
            numSpan.className = 'slot-number';
            numSpan.textContent = `Slot ${i + 1}`;
            label.appendChild(numSpan);
            slotEl.appendChild(label);

            const controls = document.createElement('div');
            controls.className = 'slot-config-controls';

            // Type select
            const typeSelect = document.createElement('select');
            typeSelect.className = 'form-control form-control-sm slot-type-select';
            typeSelect.dataset.slot = i;
            ['player', 'ai', 'blocked'].forEach((val) => {
                const opt = document.createElement('option');
                opt.value = val;
                opt.textContent = val === 'player' ? t('lobby.typePlayer') : val === 'ai' ? t('lobby.typeAI') : t('lobby.typeBlocked');
                if (val === 'player') opt.selected = true;
                typeSelect.appendChild(opt);
            });

            // Color select
            const colorSelect = document.createElement('select');
            colorSelect.className = 'form-control form-control-sm slot-ai-color hidden';
            colorSelect.dataset.slot = i;
            const autoOpt = document.createElement('option');
            autoOpt.value = '';
            autoOpt.textContent = t('lobby.aiColorAuto');
            colorSelect.appendChild(autoOpt);
            PLAYER_COLORS.forEach((c) => {
                const opt = document.createElement('option');
                opt.value = c.id;
                opt.textContent = c.name;
                opt.style.background = c.hex;
                colorSelect.appendChild(opt);
            });

            // Difficulty select
            const difficultySelect = document.createElement('select');
            difficultySelect.className = 'form-control form-control-sm slot-ai-difficulty hidden';
            difficultySelect.dataset.slot = i;
            Object.entries(DIFFICULTY_LABELS).forEach(([k]) => {
                const opt = document.createElement('option');
                opt.value = k;
                const translated = t(`ai.${k}`);
                opt.textContent = translated !== `ai.${k}` ? translated : DIFFICULTY_LABELS[k];
                if (k === 'normal') opt.selected = true;
                difficultySelect.appendChild(opt);
            });

            const idx = i;
            typeSelect.onchange = () => {
                slotConfigs[idx].type = typeSelect.value;
                colorSelect.classList.toggle('hidden', typeSelect.value !== 'ai');
                difficultySelect.classList.toggle('hidden', typeSelect.value !== 'ai');
            };
            colorSelect.onchange = () => {
                slotConfigs[idx].colorId = colorSelect.value || null;
            };
            difficultySelect.onchange = () => {
                slotConfigs[idx].aiDifficulty = difficultySelect.value;
            };

            controls.appendChild(typeSelect);
            controls.appendChild(colorSelect);
            controls.appendChild(difficultySelect);
            slotEl.appendChild(controls);
        }

        container.appendChild(slotEl);
    }

    return slotConfigs;
}
